import { Component, type ReactNode } from "react";
import { Banner, Page, Layout, Card, Text, Button } from "@shopify/polaris";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

/**
 * Error boundary that catches React render crashes.
 * Prevents the entire app from going blank (which also removes
 * the <ui-nav-menu> and kills the Shopify Admin sidebar nav).
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("React Error Boundary caught:", error, errorInfo);
    this.setState({ componentStack: errorInfo.componentStack || null });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Page title="Something went wrong">
          <Layout>
            <Layout.Section>
              <Card>
                <Banner tone="critical">
                  <p>An unexpected error occurred. Please try again.</p>
                </Banner>
                <div style={{ marginTop: 16 }}>
                  <Text as="p" tone="subdued">
                    {this.state.error?.message || "Unknown error"}
                  </Text>
                </div>
                {this.state.componentStack && (
                  <div style={{ marginTop: 16, maxHeight: 200, overflow: "auto", background: "#f5f5f5", padding: 12, borderRadius: 4, fontSize: 11, fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                    {this.state.componentStack}
                  </div>
                )}
                <div style={{ marginTop: 16 }}>
                  <Button
                    onClick={() => {
                      this.setState({ hasError: false, error: null });
                      window.location.href = "/";
                    }}
                  >
                    Go to Home
                  </Button>
                </div>
              </Card>
            </Layout.Section>
          </Layout>
        </Page>
      );
    }

    return this.props.children;
  }
}
